"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { BentoCard } from "@/components/ui/BentoCard";
import { useMountedReducedMotion } from "@/lib/useMountedReducedMotion";
import { useIsMounted } from "@/lib/useIsMounted";

const JOURNEY_STEPS = [
  {
    id: "discover",
    label: "Discover",
    desc: "We learn your vision, audience, and goals.",
    icon: "01",
  },
  {
    id: "plan",
    label: "Plan",
    desc: "A clear roadmap with priorities and timelines.",
    icon: "02",
  },
  {
    id: "create",
    label: "Create",
    desc: "Brand, design, build, and content—together.",
    icon: "03",
  },
  {
    id: "grow",
    label: "Grow",
    desc: "Launch, market, and optimize as one team.",
    icon: "04",
  },
];

const CAPABILITIES = [
  { name: "Branding & design", gradient: "from-accent/25 to-accent/5" },
  { name: "Web & mobile apps", gradient: "from-accent-strong/25 to-accent-strong/5" },
  { name: "Marketing & content", gradient: "from-accent-deep/40 to-accent-deep/10" },
  { name: "AI & automation", gradient: "from-accent/15 to-accent-deep/10" },
];

export default function HeroDemo() {
  const [stepIndex, setStepIndex] = useState(0);
  const mounted = useIsMounted();
  const reduced = useMountedReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const interval = setInterval(() => {
      setStepIndex((i) => (i + 1) % JOURNEY_STEPS.length);
    }, 3200);
    return () => clearInterval(interval);
  }, [reduced]);

  const activeStep = JOURNEY_STEPS[stepIndex];

  return (
    <BentoCard className="glass-card glow-accent p-6 sm:p-8">
      <div
        className="pointer-events-none absolute -right-20 -top-20 size-64 rounded-full bg-accent/10 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-16 -left-16 size-48 rounded-full bg-accent-deep/25 blur-3xl"
        aria-hidden
      />

      <div className="relative grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:gap-10">
        <div>
          <p className="font-ui text-sm font-semibold text-accent">How engagement works</p>
          <h3 className="font-display mt-2 text-2xl font-bold text-white sm:text-3xl">
            Brief once. Ship with one team.
          </h3>
          <p className="font-ui mt-3 text-sm leading-relaxed text-zinc-400 sm:text-base">
            Strategy, creative, and product stay on the same brief from discovery through launch.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3">
            {CAPABILITIES.map((cap, i) => (
              <motion.div
                key={cap.name}
                animate={
                  !mounted || reduced
                    ? undefined
                    : {
                        opacity: i <= stepIndex ? 1 : 0.45,
                        scale: i === stepIndex ? 1.02 : 1,
                      }
                }
                className={`glass rounded-2xl bg-gradient-to-br ${cap.gradient} px-4 py-3 transition-shadow hover:shadow-[0_0_16px_rgba(13,168,159,0.1)]`}
              >
                <p className="font-ui text-sm font-medium text-white">{cap.name}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-5 sm:p-6">
          <p className="font-ui text-xs font-medium uppercase tracking-wider text-zinc-500">
            How we work with you
          </p>

          <div className="mt-6 space-y-0">
            {JOURNEY_STEPS.map((step, i) => {
              const isActive = i === stepIndex;
              const isDone = i < stepIndex;
              return (
                <div key={step.id} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div
                      className={`font-ui flex size-10 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                        isActive
                          ? "bg-accent text-black glow-accent-strong"
                          : isDone
                            ? "bg-white/20 text-white"
                            : "border border-white/15 text-zinc-500"
                      }`}
                    >
                      {isDone ? "✓" : step.icon}
                    </div>
                    {i < JOURNEY_STEPS.length - 1 && (
                      <div
                        className={`my-1 min-h-[24px] w-px flex-1 ${isDone ? "bg-accent/50" : "bg-white/10"}`}
                      />
                    )}
                  </div>
                  <div className={`pb-6 transition-opacity duration-300 ${isActive ? "" : "opacity-60"}`}>
                    <p className="font-display font-semibold text-white">{step.label}</p>
                    <p className="font-ui mt-1 text-sm text-zinc-400">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep.id}
              initial={mounted && !reduced ? { opacity: 0, y: 8 } : false}
              animate={{ opacity: 1, y: 0 }}
              exit={mounted && !reduced ? { opacity: 0, y: -8 } : undefined}
              transition={{ duration: 0.25 }}
              className="mt-2 rounded-xl border border-accent/20 bg-accent/5 px-4 py-3 glow-accent"
            >
              <p className="font-ui text-sm text-zinc-300">
                <span className="font-semibold text-accent">Right now:</span> {activeStep.desc}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </BentoCard>
  );
}
